import { NgModule } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import localeDe from '@angular/common/locales/de';

import { TranslateModule } from '@ngx-translate/core';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';
import { ToastrModule } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ModalModule } from 'ngx-bootstrap/modal';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { LottieComponent, provideCacheableAnimationLoader, provideLottieOptions } from 'ngx-lottie';

// layout chrome
import { HeaderComponent } from '@core/layouts/header/header.component';
import { SidebarComponent } from '@core/layouts/sidebar/sidebar.component';
import { SiteFooterComponent } from '@core/layouts/site-footer/site-footer.component';

// UI kit
import { BwsAlertComponent } from '@shared/components/alert/bws-alert.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { IconListItemComponent } from '@shared/components/icon-list-item/icon-list-item.component';
import { MdiComponent } from '@shared/components/mdi.component';
import { PageLoaderComponent } from '@shared/components/page-loader.component';
import { StepperComponent } from '@shared/components/stepper/stepper.component';
import { StepComponent } from '@shared/components/stepper/step.component';
import { CommunityToastComponent } from '@shared/community-toast/community-toast.component';
import { HeaderContentComponent } from '@shared/header-content/header-content.component';

import { BwsButtonSpinnerDirective } from '@shared/directives/button-spinner.directive';
import { BwsClickOutsideDirective } from '@shared/directives/click-outside.directive';
import { StickyDirective } from '@shared/directives/sticky.directive';

import { BytesPipe } from '@shared/pipes/bytes.pipe';
import { CeilPipe } from '@shared/pipes/ceil.pipe';
import { CurrencyFormatPipe } from '@shared/pipes/currency-format.pipe';
import { HighlightSearchPipe } from '@shared/pipes/highlight-search.pipe';
import { NumberFormatPipe } from '@shared/pipes/number-format.pipe';
import { SafePipe } from '@shared/pipes/safe.pipe';
import { SlugPipe } from '@shared/pipes/slug.pipe';
import { TranslateCutPipe } from '@shared/pipes/translate-cut.pipe';
import { TruncatePipe } from '@shared/pipes/truncate.pipe';

import { GtagModule } from '@shared/gtag/gtag.module';

/**
 * The application's UI kit: layout chrome, shared components, directives and pipes,
 * plus the third-party modules that feature templates rely on.
 *
 * This used to be split between the app's own SharedModule and BwsSharedModule /
 * BwsLayoutModule from the packaged shared library. The library is compiled from source
 * now, so the two live here as one module — feature modules import SharedModule alone.
 */
const COMPONENTS = [
  // layout chrome
  HeaderComponent,
  SidebarComponent,
  SiteFooterComponent,

  // UI kit
  BwsAlertComponent,
  BreadcrumbComponent,
  IconListItemComponent,
  MdiComponent,
  PageLoaderComponent,
  StepperComponent,
  StepComponent,

  // app specific
  CommunityToastComponent,
  HeaderContentComponent,
];

const DIRECTIVES = [
  BwsButtonSpinnerDirective,
  BwsClickOutsideDirective,
  StickyDirective,
];

const PIPES = [
  BytesPipe,
  CeilPipe,
  CurrencyFormatPipe,
  HighlightSearchPipe,
  NumberFormatPipe,
  SafePipe,
  SlugPipe,
  TranslateCutPipe,
  TruncatePipe,
];

@NgModule({
  declarations: [
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES,
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,

    // imported, not just re-exported: the components declared here use the
    // translate / localize pipes in their own templates
    TranslateModule,
    LocalizeRouterModule,

    ToastrModule.forRoot(),
    GtagModule.forRoot({
      trackingId: '',
      trackPageviews: true,
    }),
    LottieComponent,

    BsDropdownModule,
    CollapseModule,
    TabsModule,
    ModalModule,
    AccordionModule,
  ],
  providers: [
    ConfirmationService,
    // lazy import keeps lottie-web out of the SSR bundle (it touches `document` at load time)
    provideLottieOptions({ player: () => import('lottie-web/build/player/lottie_svg') }),
    provideCacheableAnimationLoader(),
  ],
  exports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,

    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES,

    TranslateModule,
    LocalizeRouterModule,
    ToastrModule,
    GtagModule,
    LottieComponent,

    BsDropdownModule,
    CollapseModule,
    TabsModule,
    TooltipModule,
    ModalModule,
    AccordionModule,

    // NOTE: ng-gallery / ng-gallery-lightbox are deliberately NOT re-exported here.
    // SharedModule is eager (CoreModule -> AppModule), so re-exporting them dragged
    // ~87 kB into the initial bundle for every visitor. The two pages that use a
    // gallery (home, help-pages) import GalleryModule/LightboxModule themselves.
    //
    // The rich text editor is likewise separate (EditorModule): its component style
    // inlines ~213 kB of CKEditor CSS.
  ],
})
export class SharedModule {
  constructor() {
    /* register additional, german locale data */
    registerLocaleData(localeDe);
  }
}
